package org.multiplatform.kotfolio.pages.flutter

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.FontSize
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.*
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ul
import org.multiplatform.kotfolio.components.KotlinCode
import org.multiplatform.kotfolio.layout.BlogLayout
import org.multiplatform.kotfolio.layout.BlogProperties
import org.multiplatform.kotfolio.layout.PageProperties

@Page("/flutter/null_safety")
@Composable
fun DartNullSafetyPage() {
    BlogLayout(
        PageProperties(
            "Null Aware Operators | Null Safety",
            "Null safety is a most important feature in Dart that helps to avoid null reference errors, also support many programming languages. Know about Dart Null Safety in Flutter in detail."
        ),
        BlogProperties("Dart", "Null Aware Operators | Null Safety")
    ) {
        Column(
            horizontalAlignment = Alignment.Start
        ) {
            SpanText(
                text = "?? Safe Navigation Operator: If-null operator",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Called also null operator. This operator returns expression on its left, except if it is null, and if so, it returns right expression.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    String? firstName;
                    print(firstname ?? 'Hello');

                    //output: Hello

                    String? firstName = 'Flutter';
                    print(firstname ?? 'Hello');

                    //output: Flutter
                """.trimIndent()
            )

            SpanText(
                text = "??= Null-aware assignment operator",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Called also null-aware assignment. This operator assigns value to the variable on its left, only if that variable is currently null.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    void main() {
                      String? input;
                      input ??= 'Flutter';

                      print(input);
                    }

                    //OUTPUT: FLUTTER

                    void main() {
                      String? input = '';
                      input ??= 'Flutter';

                      print(input);
                    }

                    //OUTPUT: RUN TIME ERROR
                """.trimIndent()
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This operator is also called the (if-null) operator, and easy as it sounds, it checks if a variable is null or not.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    String? firstName;
                    print(firstname ?? 'Hello');

                    //output: Hello

                    String? firstName = 'Flutter';
                    print(firstname ?? 'Hello');

                    //output: Flutter
                    """.trimIndent()
            )

            SpanText(
                text = "?. Safe Navigation Operator",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Null-aware access & method invocation operator: We will navigate to the .toUpperCase() method safely by changing an.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    void main() {
                      String? input;
                      print(input?.length); // null
                      print(input?.toLowerCase()); // null

                      print(input.toLowerCase()); // <- will crash
                      print(input?.toLowerCase().toUpperCase());  // <- will crash
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "! Null assertion operator",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "We use it to say that the variable defined cannot be null, that we are sure that it will always return a valid value.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    bool? isTextFile(String? filename) {
                      if (filename != null) {
                        return filename.endsWith('.dart') ? true : false;
                      }
                      return null;
                    }

                    void main() {
                      bool result = isTextFile('flutter')!;
                      print(result);
                    }

                    //OUTPUT: false
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    bool? isTextFile(String? filename) {
                      if (filename != null) {
                        return filename.endsWith('.txt') ? true : false;
                      }
                      return null;
                    }

                    void main() {
                      bool result = isTextFile('readme.txt');
                      print(result);
                    }

                    //OUTPUT: NOT RUN 
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    void main() {
                      String? name;
                      print(name!.length);
                    }

                    //OUTPUT: RUN TIME EXCEPTION

                    void main() {
                      String? nullableVariable = null;
                      print(nullableVariable!.length); // This will throw a runtime error
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "?.. Null-aware cascade operator",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            SpanText(
                text = "?[] Null-aware index operator",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    void main() {
                      List? scores = [1, 2, 3, 4, 5];
                      // somewhere in the code
                      scores = null;
                      print(scores?[3]); // null
                    }

                    //OUTPUT: null
                    """.trimIndent()
            )

            SpanText(
                text = "…? Null-aware spread operator",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This operator prevents you from adding null elements using the spread operator(lets you add multiple elements into your collection).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    void main() {
                      List<int> list = [1, 2, 3];
                      List<String> list2; // <- list2 is null
                      print(['chocolate', ...?list2]); // <- [chocolate]
                      print([0, ...?list2, ...list]); // <- [0, 1, 2, 3]
                      print(['cake!', ...list2]);  // <- will crash
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    void main(){
                      List<int> lowerNumber = [1,2,3,4,5];
                      List<int> upperNumbers = [6,8,9,0];

                      //NULL NOT ALLOWED
                      lowerNumber = [...lowerNumber, ...upperNumbers];
                      print(lowerNumber);

                      //NULL ALLOWED
                      List<int>? listNull;
                      lowerNumber = [...lowerNumber, ...?listNull];
                      print(lowerNumber);
                    }

                    //OUTPUT
                    //numbers are [1, 2, 3, 4, 5, 6, 8, 9, 0]
                    //new list are [1, 2, 3, 4, 5, 6, 8, 9, 0]
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    void main() {
                      List<Friend> friendA = [
                        Friend(name: 'Kamlesh', age: 12),
                        Friend(name: 'Sagar', age: 17)
                      ];
                      List<Friend>? friendB;
                      List<Friend> myFriends = [
                        ...friendA,
                        ...?friendB,
                        Friend(name: 'Rahul', age: 15)
                      ];
                      myFriends.forEach((friend) => print(friend.name));
                    }

                    class Friend {
                      String? name;
                      int? age;
                      Friend({this.name, this.age});
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "… Spread Operator",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                     Map<String, int> map1 = {'a': 10, 'b': 20};
                     Map<String, int> map2 = {'c': 30, 'd': 40, 'FlutterDevs.com': 50};

                     Map<String, int> mergedMap = {...map1, ...map2};
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    Column(children: [
                        ...UsbInterface.values.map(
                        (value) => widget())
                    ]    
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    if() ...[] else ...[]
                    """.trimIndent()
            )

            SpanText(
                text = "Non-null promotion for private final fields",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    class Container {
                      final int? _fillLevel;
                      Container(this._fillLevel);
                      check() {
                        if (_fillLevel != null) {
                          int i = _fillLevel; // Prior to Dart 3.2, causes an error.
                        }
                      }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "? : Ternary operator",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    void main() {
                      print(2 == 2 ? "a truth" : "a lie"); // <- a truth
                      print(1 == 2 ? "a truth" : "a lie"); // <- a lie  5 == 6 ? doThis() : doThat(); // <- done that
                    }void doThis() {
                      print('done this');
                    }void doThat() {
                      print('done that');
                    }
                    """.trimIndent()
            )
        }
    }
}

